import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Modal, ModalBody } from "reactstrap"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import { mapReviewDetail } from "./adminCityReviewsData"

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
}

/**
 * View admin review detail — GET /api/v1/admin/reviews/:id
 */
const ReviewDetailModal = ({ isOpen, onClose, reviewId, onEdit }) => {
  const [detail, setDetail] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen || !reviewId) return undefined

    let cancelled = false

    const loadDetail = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await get(endpoints.getAdminReviewById(reviewId))
        if (cancelled) return
        if (response?.data?.success) {
          setDetail(mapReviewDetail(response.data.result || {}))
        } else {
          const message = toastApiError(response, "Failed to load review")
          setError(message)
          setDetail(null)
        }
      } catch (err) {
        if (!cancelled) {
          const message = toastApiError(err, "Failed to load review")
          setError(message)
          setDetail(null)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadDetail()
    return () => {
      cancelled = true
    }
  }, [isOpen, reviewId])

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      size="lg"
      className="amok-modal"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body admin-city-reviews__detail-body">
        <div className="admin-city-reviews__detail-head">
          <h3 className="edit-profile__modal-title">Review Details</h3>
          <div className="admin-city-reviews__detail-actions">
            {detail?.id && onEdit ? (
              <CustomButton
                variant="secondary"
                size="sm"
                type="button"
                onClick={() => onEdit(detail)}
              >
                Edit
              </CustomButton>
            ) : null}
            <CustomButton
              variant="danger"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Close
            </CustomButton>
          </div>
        </div>

        {isLoading ? (
          <div className="admin-city-reviews__detail-skeleton" aria-busy="true">
            <div className="admin-city-reviews__skeleton admin-city-reviews__skeleton--title" />
            <div className="admin-city-reviews__skeleton admin-city-reviews__skeleton--line" />
            <div className="admin-city-reviews__skeleton admin-city-reviews__skeleton--line" />
            <div className="admin-city-reviews__skeleton admin-city-reviews__skeleton--media" />
          </div>
        ) : null}

        {!isLoading && error ? (
          <EmptyState title="Couldn’t load review" description={error} />
        ) : null}

        {!isLoading && detail ? (
          <div className="admin-city-reviews__detail">
            <div className="admin-city-reviews__detail-meta">
              <span
                className={`admin-city-reviews__status admin-city-reviews__status--${detail.status}`}
              >
                {STATUS_LABEL[detail.status] || detail.status}
              </span>
              <span>{detail.date}</span>
              <span>{detail.cityLabel}</span>
            </div>
            <h4 className="admin-city-reviews__detail-title">{detail.title}</h4>
            <p className="admin-city-reviews__detail-reviewer">
              By {detail.reviewer}
            </p>
            <p className="admin-city-reviews__detail-description">
              {detail.description || "—"}
            </p>
            {detail.medias?.length ? (
              <ul className="admin-city-reviews__media-grid">
                {detail.medias.map(media => (
                  <li key={media.id}>
                    <img src={media.media_url} alt="" />
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}
      </ModalBody>
    </Modal>
  )
}

ReviewDetailModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  reviewId: PropTypes.string,
  onEdit: PropTypes.func,
}

ReviewDetailModal.defaultProps = {
  reviewId: undefined,
  onEdit: undefined,
}

export default ReviewDetailModal
